import { Box, Button, Heading, Input, Stack, Text, ThemeContext } from '@vouched.id/vault';
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { VouchedLogo } from 'components/reusable/vouched-logo';
import { set2FAData, setAuthData, setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { createAccount, isSingleUseSignupTokenAvailable } from 'store/sagas/sagas';
import { humanDate } from 'utils/ui.utils';
import { formatInput } from 'utils/validation';
const getQueryVariable = (searchQuery, variable) => {
    for (const v of searchQuery.split('&')) {
        const [key = '', value = ''] = v.split('=');
        if (decodeURIComponent(key) === variable) {
            return decodeURIComponent(value);
        }
    }
    console.warn('Query variable %s not found', variable);
    return undefined;
};
var FieldState;
(function (FieldState) {
    FieldState[FieldState["NOT_SET"] = 0] = "NOT_SET";
    FieldState[FieldState["VALID"] = 1] = "VALID";
    FieldState[FieldState["INVALID"] = 2] = "INVALID";
})(FieldState || (FieldState = {}));
export const CreateAccount = () => {
    const query = useLocation().search.slice(1);
    const tier = getQueryVariable(query, 'tier');
    const token = getQueryVariable(query, 'token');
    const { dispatch, asyncDispatch } = useGlobalState();
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState({ email: '', password: '' });
    const [isInputInvalid, setIsInputInvalid] = useState({
        email: FieldState.NOT_SET,
        password: FieldState.NOT_SET
    });
    const { tokens } = useContext(ThemeContext).theme;
    const desktopLogoColor = tokens.colors.backgroundPrimary;
    const handleChange = (e) => {
        const { name } = e.target;
        let { value } = e.target;
        if (name === 'email') {
            const { formattedValue } = formatInput(e);
            value = formattedValue;
        }
        setUserProfile((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };
    useEffect(() => {
        if (token && token.trim() !== '') {
            asyncDispatch(isSingleUseSignupTokenAvailable(token))
                .then((result) => {
                if (!result.available) {
                    dispatch(setMessages({
                        severity: 'error',
                        value: 'This link is no longer valid'
                    }));
                }
            })
                .catch(console.error);
        }
    }, [token]);
    const handleBlur = (e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            if (value === '') {
                setIsInputInvalid({ ...isInputInvalid, [name]: FieldState.INVALID });
            }
            else {
                const { isValid } = formatInput(e);
                setIsInputInvalid({
                    ...isInputInvalid,
                    [name]: isValid ? FieldState.VALID : FieldState.INVALID
                });
            }
        }
    };
    const [isLoading, setIsLoading] = useState(false);
    const submit = (event) => {
        event.preventDefault();
        const { email } = userProfile;
        const { password } = userProfile;
        if (email && password && tier) {
            setIsLoading(true);
            asyncDispatch(createAccount({ email, password, tier, token }))
                .then((data) => {
                setIsLoading(false);
                if ('otpToken' in data) {
                    dispatch(set2FAData(data));
                    navigate('/verify-2fa');
                }
                else {
                    if (data.user.loginAt) {
                        dispatch(setMessages({
                            severity: 'info',
                            value: `Last login at ${humanDate(data.user.loginAt)}`
                        }));
                    }
                    dispatch(setAuthData(data));
                    navigate('/account/needs-verification');
                }
            })
                .catch(() => {
                setIsLoading(false);
                setIsInputInvalid({
                    email: FieldState.INVALID,
                    password: FieldState.INVALID
                });
            });
        }
    };
    // eventually update vault theme
    const midPurple = '#453FC1';
    const brightGreen = '#9FEA99';
    const darkGreen = '#64A782';
    useLayoutEffect(() => {
        document.title = 'Vouched | Create Account Page';
    }, []);
    return (React.createElement(Box, { display: "flex", padding: ['spacing.large', 'spacing.none'], style: {
            boxSizing: 'border-box',
            backgroundColor: midPurple,
            minHeight: '100vh'
        }, tag: "main" },
        React.createElement(Box, { alignItems: "center", backgroundColor: "colors.backgroundPrimary", display: "flex", flex: ['1', '1 1 50%'], flexDirection: "column", justifyContent: "center", style: {
                borderRadius: ['spacing.default', 'spacing.none'],
                boxSizing: 'border-box'
            } },
            React.createElement(Box, { maxWidth: ['75%', null, '60%'], paddingY: "spacing.large" },
                React.createElement(Box, { display: ['block', 'none'], marginBottom: "spacing.xlarge" },
                    React.createElement(VouchedLogo, { fill: midPurple, maxWidth: "none", width: "100%" }),
                    React.createElement(Text, { style: {
                            color: darkGreen,
                            fontWeight: '600',
                            fontSize: ['text.size.default'],
                            marginTop: 'spacing.xsmall2',
                            textAlign: 'center'
                        } }, "Self Serve")),
                React.createElement(Heading, { style: {
                        fontSize: ['text.size.large', null, 'text.size.xlarge2'],
                        fontWeight: '400',
                        color: midPurple,
                        textAlign: 'center',
                        marginBottom: 'spacing.default'
                    }, tag: "h1" }, "Welcome"),
                React.createElement(Heading, { style: {
                        fontWeight: 'normal',
                        fontSize: ['text.size.default', null, '24px'],
                        textAlign: 'center',
                        marginBottom: 'spacing.xlarge'
                    }, tag: "h2", variation: "h5" }, "Start verifying your customers by creating an account with a 14-day free trial."),
                React.createElement(Box, { marginBottom: "spacing.xlarge", noValidate: true, onSubmit: submit, tag: "form", width: "100%" },
                    React.createElement(Stack, { spacing: "spacing.large" },
                        React.createElement(Box, null,
                            React.createElement(Input, { autoComplete: "email", inputMode: "email", isInvalid: isInputInvalid['email'] === FieldState.INVALID, label: "Email", name: "email", onBlur: handleBlur, onChange: handleChange, validationMessage: "Invalid email address." })),
                        React.createElement(Box, null,
                            React.createElement(Input, { inputMode: "text", label: "Password", name: "password", onChange: handleChange, type: "password", validationMessage: "Invalid password." })),
                        React.createElement(Text, { style: { textAlign: 'right', fontSize: 'text.size.xsmall' } },
                            React.createElement("a", { href: "/signin", style: { color: midPurple } }, "Already have an account?")),
                        React.createElement(Box, { alignItems: "center", display: "flex", justifyContent: "center", marginTop: "spacing.default", paddingTop: "spacing.default", width: "100%" },
                            React.createElement(Button, { "data-cy": "submit", style: { backgroundColor: midPurple }, title: isLoading ? 'loading' : '' }, "Create Account")))),
                React.createElement(Box, { paddingY: "spacing.small", style: { borderTop: '1px solid lightgray' } },
                    React.createElement(Text, { style: { textAlign: 'center', fontSize: 'text.size.xsmall' } },
                        "Want more information?",
                        ' ',
                        React.createElement("a", { href: "https://vouched.id", style: { color: midPurple } }, "Check out our site"),
                        ' ',
                        "or you can",
                        ' ',
                        React.createElement("a", { href: "https://www.vouched.id/get-started", style: { color: midPurple } }, "Request a Demo"),
                        ".")))),
        React.createElement(Box, { alignItems: "center", display: ['none', 'flex'], flex: "1 1 50%", flexDirection: "column", justifyContent: "center" },
            React.createElement(VouchedLogo, { fill: desktopLogoColor, maxWidth: "50rem", width: "80%" }),
            React.createElement(Text, { colorVariation: "inversePrimary", style: {
                    color: brightGreen,
                    fontWeight: '600',
                    fontSize: ['text.size.default', null, 'text.size.large'],
                    marginTop: 'spacing.large'
                } }, "Self Serve"))));
};
